"use client";

import { Button, Flex, Spinner, Timeline } from "@chakra-ui/react";
import { formatTimestamp } from "helpers/formatTimestamp";
import { memo, useCallback, useMemo, useState } from "react";
import { LuCheck, LuClock, LuPlay } from "react-icons/lu";

interface TimelineStepListProps {
  steps: readonly { id: string; label: string }[];
  events: { event: string; timestamp: Date }[];
  onMarkComplete?: (eventId: string) => Promise<void>;
}

const TimelineStepList = memo(
  ({ steps, events, onMarkComplete }: TimelineStepListProps) => {
    const [triggeringId, setTriggeringId] = useState<string | null>(null);

    const completedMap = useMemo(
      () => new Map(events.map((e) => [e.event, e.timestamp])),
      [events],
    );

    const nextEventId = steps.find((step) => !completedMap.has(step.id))?.id;

    const onClickMarkComplete = useCallback(async () => {
      if (nextEventId == null || onMarkComplete == null) {
        return;
      }
      setTriggeringId(nextEventId);
      try {
        await onMarkComplete(nextEventId);
      } finally {
        setTriggeringId(null);
      }
    }, [nextEventId, onMarkComplete]);

    return (
      <Timeline.Root size="sm">
        {steps.map((step) => {
          const timestamp = completedMap.get(step.id);
          const isCompleted = timestamp != null;
          const isNext = step.id === nextEventId;
          const isTriggering = triggeringId === step.id;
          const canMark = isNext && onMarkComplete != null;

          return (
            <Timeline.Item key={step.id}>
              <Timeline.Connector>
                <Timeline.Separator borderColor="border.light" />
                <Timeline.Indicator
                  bg={isCompleted ? "green.solid" : "transparent"}
                  color={isCompleted ? "green.contrast" : "fg.muted"}
                >
                  {isCompleted ? <LuCheck /> : <LuClock />}
                </Timeline.Indicator>
              </Timeline.Connector>
              <Timeline.Content>
                <Flex align="center" gap={3}>
                  <Flex direction="column">
                    <Timeline.Title
                      color={isCompleted ? "text.primary" : "text.secondary"}
                    >
                      {step.label}
                    </Timeline.Title>
                    {isCompleted && (
                      <Timeline.Description>
                        {formatTimestamp(timestamp)}
                      </Timeline.Description>
                    )}
                  </Flex>
                  {canMark && (
                    <Button
                      size="2xs"
                      variant="subtle"
                      colorPalette="green"
                      onClick={onClickMarkComplete}
                      disabled={triggeringId != null}
                    >
                      {isTriggering ? (
                        <Spinner size="xs" />
                      ) : (
                        <>
                          <LuPlay />
                          Mark complete
                        </>
                      )}
                    </Button>
                  )}
                </Flex>
              </Timeline.Content>
            </Timeline.Item>
          );
        })}
      </Timeline.Root>
    );
  },
);

TimelineStepList.displayName = "TimelineStepList";

export default TimelineStepList;
